import React, {
	FC,
	memo,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useWindowSize } from 'react-use';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { NavHashLink } from 'react-router-hash-link';
import { Manager, Popper, Reference } from 'react-popper';
// @ts-ignore
import useEventOutside from '@omtanke/react-use-event-outside';
import Collapse from '../../components/bootstrap/Collapse';
import Icon from '../../components/icon/Icon';
import ThemeContext from '../../contexts/themeContext';
import useDarkMode from '../../hooks/useDarkMode';
import { TIcons } from '../../type/icons-type';
import List from './List';

interface IItemProps {
	children?: ReactNode;
	to?: string;
	title?: string;
	icon?: TIcons;
	id?: string;
	parentId?: string;
	rootId: string;
	isHorizontal?: boolean;
	notification?: boolean | string | number;
	isMore?: boolean;
	hide?: boolean;
	activeItem?: string;
	setActiveItem?(...args: unknown[]): unknown;
}
const Item: FC<IItemProps> = ({
	children,
	to,
	title,
	icon,
	id,
	parentId,
	rootId,
	isHorizontal,
	notification,
	isMore,
	hide,
	...props
}) => {
	const { darkModeStatus } = useDarkMode();
	const { width } = useWindowSize();
	const { setAsideStatus, setLeftMenuStatus, setRightMenuStatus } = useContext(ThemeContext);
	const navigate = useNavigate();

	const ACTIVE = props.activeItem === id;

	const handleClick = () => {
		if (typeof props.setActiveItem !== 'undefined') {
			ACTIVE ? props.setActiveItem(null) : props.setActiveItem(id);
		}
	};

	const linkHandleClick = () => {
		// For Mobile Design
		if (width < Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE)) setAsideStatus(false);
		setLeftMenuStatus(false);
		setRightMenuStatus(false);
	};

	const ANCHOR_LINK_PATTERN = /^#/i;
	const location = useLocation();

	// For aside menu
	// const here = typeof to === 'string' && to !== '/' && location.pathname.includes(to);

	// For aside menu
	const here =
		typeof to === 'string' &&
		to !== '/' &&
		(location.pathname === to || location.pathname.startsWith(`${to}/`));

	// For top menu
	const match = to !== '/' && location.pathname === to;

	const { t } = useTranslation('menu');

	const LINK_CLASS = classNames('navigation-link', 'navigation-link-pill', {
		collapsed: !!children && !isHorizontal,
		active: isHorizontal ? match : here,
	});

	// For parent active item
	const isParentActive =
		to && (location.pathname === `/${to}` || location.pathname.startsWith(`/${to}/`));

	const INNER = (
		<>
			<span className='navigation-link-info'>
				{icon && <Icon className='navigation-icon' icon={icon} />}
				{title && <span className='navigation-text'>{t(title) as ReactNode}</span>}
			</span>
			{(!!children || !!notification) && (
				<span className='navigation-link-extra'>
					{!!notification && (
						<Icon
							icon='Circle'
							className={classNames(
								'navigation-notification',
								{
									[`text-${notification}`]: typeof notification === 'string',
									'text-danger': typeof notification !== 'string',
								},
								'animate__animated animate__heartBeat animate__infinite animate__slower',
							)}
						/>
					)}
					{!!children && <Icon className='navigation-arrow' icon='ChevronRight' />}
				</span>
			)}
		</>
	);

	const WITHOUT_CHILD =
		!children &&
		!hide &&
		((typeof to === 'string' && ANCHOR_LINK_PATTERN.test(to) && (
			// @ts-ignore
			<NavHashLink className={LINK_CLASS} to={to} onClick={linkHandleClick}>
				{INNER}
			</NavHashLink>
		)) || (
				<NavLink
					end
					// @ts-ignore
					className={classNames(LINK_CLASS, ({ isActive }) => (isActive ? 'active' : ''))}
					to={`../${to}`}
					onClick={linkHandleClick}>
					{INNER}
				</NavLink>
			));

	// Dropdown
	const dropdownRef = useRef(null);

	const dropdownButtonRef = useRef(null);
	const setButtonRef = useCallback((node: null, ref: (arg0: any) => any) => {
		dropdownButtonRef.current = node;
		return ref(node);
	}, []);

	const dropdownListRef = useRef(null);
	const setListRef = useCallback((node: null, ref: (arg0: any) => any) => {
		dropdownListRef.current = node;
		return ref(node);
	}, []);

	const [dropdownStatus, setDropdownStatus] = useState(false);

	const dropdownButtonHandleClick = () => {
		setDropdownStatus(!dropdownStatus);
	};

	// Clicking outside to close
	const closeMenu = useCallback(() => {
		setDropdownStatus(false);
	}, []);
	useEventOutside(dropdownRef, 'mousedown', closeMenu);
	useEventOutside(dropdownRef, 'touchstart', closeMenu);

	const [localDropdownOpen, setLocalDropdownOpen] = useState(false);

	useEffect(() => {
		if (!to) return;

		const base = to.startsWith('/') ? to : `/${to}`;
		const underThisParent =
			location.pathname === base || location.pathname.startsWith(`${base}/`);

		if (underThisParent && location.pathname !== base) {
			setLocalDropdownOpen(true);
		} else {
			setLocalDropdownOpen(false);
		}
	}, [location.pathname, to]);

	// handleMouseLeave for regular hover-based close
	const handleMouseLeave = () => {
		if (!to) return;

		const base = to.startsWith('/') ? to : `/${to}`;
		const underThisParent =
			location.pathname === base || location.pathname.startsWith(`${base}/`);

		if (!underThisParent) {
			setLocalDropdownOpen(false);
		}
	};

	if (children) {
		// submenu && in header
		if (isHorizontal) {
			return (
				<Manager>
					<li
						ref={dropdownRef}
						className={classNames('navigation-item', 'dropdown', {
							'navigation-item-more': isMore,
						})}>
						<Reference>
							{({ ref }) => (
								<div id={`${rootId}__${id}--link`} className={LINK_CLASS}>
									<NavLink
										end
										className={({ isActive }) =>
											classNames('navigation-link-info', { active: isActive })
										}
										to={to || '#'}
										onClick={linkHandleClick}>
										{icon && <Icon className='navigation-icon' icon={icon} />}
										{title && (
											<span className='navigation-text'>
												{t(title) as ReactNode}
											</span>
										)}
									</NavLink>

									<span
										// @ts-ignore
										ref={(node) => setButtonRef(node, ref)}
										id={`${rootId}__${id}--link`}
										className={LINK_CLASS}
										// data-bs-toggle='dropdown'
										// data-bs-target={`#${rootId}__${id}`}
										aria-expanded={dropdownStatus}
										aria-controls={`${rootId}__${id}`}
										role='button'
										tabIndex={-1}
										onClick={dropdownButtonHandleClick}
										onKeyDown={dropdownButtonHandleClick}>
										{INNER}
									</span>
								</div>
							)}
						</Reference>
						{dropdownStatus && (
							<Popper
								placement='bottom-start'
								modifiers={[
									{
										name: 'flip',
										options: {
											fallbackPlacements: [`bottom-end`, `bottom-start`],
										},
									},
								]}>
								{({ ref, style, placement }) => (
									<List
										// @ts-ignore
										ref={(node) => setListRef(node, ref)}
										style={style}
										data-placement={placement}
										id={`${rootId}__${id}`}
										className={classNames(
											'dropdown-menu',
											{
												'dropdown-menu-dark': darkModeStatus,
											},
											'show',
										)}
										ariaLabelledby={`${rootId}__${id}--link`}
										rootId={rootId}
										parentId={`${rootId}__${parentId}`}
										onMouseLeave={() => setDropdownStatus(false)}>
										{children}
									</List>
								)}
							</Popper>
						)}
					</li>
				</Manager>
			);
		}

		// submenu && in aside
		if (!isHorizontal) {
			if (
				id === 'account' ||
				id === 'company' ||
				id === 'product' ||
				id === 'category' ||
				id === 'product_unit' ||
				id === 'requisition' ||
				id === 'purchase_indent' ||
				id === 'quotation' ||
				id === 'purchase_order' ||
				id === 'grn' ||
				id === 'purchase_bill' ||
				id === 'payment_type' ||
				id === 'payment' ||
				id === 'payment_term' ||
				id === 'payment_verification' ||
				id === 'payment_step' ||
				id === 'place_of_supply' ||
				id === 'staff' ||
				id === 'area' ||
				id === 'biometric_device' ||
				id === 'staffSalary' ||
				id === 'cams' ||
				id === 'inventory_management' ||
				id === 'po_modify_request' ||
				id === 'stock_adjustment' ||
				id === 'stock_transfer' ||
				id === 'attendance' ||
				id === 'holiday_management' ||
				id === 'salary_management' ||
				id === 'celebration_management' ||
				id === 'allowance_management' ||
				id === 'notificationConfig' ||
				id === 'stock_master' ||
				id === 'site_verification_checklist' ||
				id === 'warehouse' ||
				id === 'site_verification' ||
				id === 'site_management' ||
				id === 'resources' ||
				id === 'staffPunchData' ||
				id === 'staff_attendance_report' ||
				id === 'salaryStructureManagement' ||
				id === 'shiftManagement' ||
				id === 'liveResourcesByCompanyWarehouse' ||
				id === 'contract_management' ||
				id === 'resourceAllocation' ||
				id === 'noc' ||
				id === 'vehicle_management' ||
				id === 'inquiry_management' ||
				id === 'staffPaySlipAndAttendance' ||
				id === 'requestManagement' ||
				id === 'attendanceMonthlyReport' ||
				id === 'day_book' ||
				id === 'expenses_and_bills' ||
				id === 'notification' ||
				id === 'po_modify_request_list' ||
				id === 'hrm' ||
				id === 'verification_process' ||
				id === 'quick_payment'
			) {
				return (
					<li className='navigation-item'>
						<div
							id={`${rootId}__${id}--link`}
							className={classNames(LINK_CLASS, { active: isParentActive })}
							// aria-expanded={ACTIVE}
							aria-expanded={localDropdownOpen}
							aria-controls={`${rootId}__${id}`}>
							{/* TEXT: navigate to the list page */}
							<NavLink
								end
								to={`/${to}`}
								className='navigation-link-info'
								style={{ textDecoration: 'none', color: 'inherit' }}
								onClick={(e) => {
									if (
										width < Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE)
									) {
										// Only close sidebar on mobile
										linkHandleClick();
									}
									// Desktop: just navigate, don’t auto-close aside
								}}>
								{icon && <Icon className='navigation-icon' icon={icon} />}
								{title && (
									<span className='navigation-text'>{t(title) as ReactNode}</span>
								)}
							</NavLink>

							{/* ARROW: only toggles dropdown */}
							{/* <span
								className='navigation-link-extra'
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setLocalDropdownOpen((prev) => !prev);
								}}>
								{!!notification && (
									<Icon
										icon='Circle'
										className={classNames(
											'navigation-notification',
											{
												[`text-${notification}`]:
													typeof notification === 'string',
												'text-danger': typeof notification !== 'string',
											},
											'animate__animated animate__heartBeat animate__infinite animate__slower',
										)}
									/>
								)}

								<Icon
									className={classNames('navigation-arrow', {
										open: localDropdownOpen,
									})}
									icon='ChevronRight'
								/>
							</span> */}
							<span
								className='navigation-link-extra'
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setLocalDropdownOpen((prev) => !prev);
								}}>
								{notification && Number(notification) > 0 && (
									<span
										className={classNames(
											'badge rounded-pill bg-danger me-2 animate__animated animate__heartBeat animate__infinite animate__slower',
										)}
										style={{ fontSize: '0.65rem', padding: '0.35em 0.65em' }}>
										{/* Cast to Number here to satisfy TypeScript */}
										{Number(notification) > 99 ? '99+' : notification}
									</span>
								)}

								<Icon
									className={classNames('navigation-arrow', {
										open: localDropdownOpen,
									})}
									icon='ChevronRight'
								/>
							</span>
						</div>

						{/* DROPDOWN LIST */}
						<Collapse isOpen={localDropdownOpen} isChildClone>
							<List
								id={`${rootId}__${id}`}
								ariaLabelledby={`${rootId}__${id}--link`}
								rootId={rootId}
								parentId={`${rootId}__${parentId}`}
								onMouseLeave={handleMouseLeave}>
								{children}
							</List>
						</Collapse>
					</li>
				);
			}

			return (
				<li className='navigation-item'>
					<span
						id={`${rootId}__${id}--link`}
						className={LINK_CLASS}
						// data-bs-toggle='collapse'
						// data-bs-target={`#${rootId}__${id}`}
						aria-expanded={ACTIVE}
						aria-controls={`${rootId}__${id}`}
						role='button'
						tabIndex={-1}
						onClick={handleClick}
						onKeyDown={handleClick}>
						{INNER}
					</span>
					<Collapse isOpen={ACTIVE} isChildClone>
						<List
							id={`${rootId}__${id}`}
							ariaLabelledby={`${rootId}__${id}--link`}
							rootId={rootId}
							parentId={`${rootId}__${parentId}`}
							onMouseLeave={closeMenu}>
							{children}
						</List>
					</Collapse>
				</li>
			);
		}
	}

	// without submenu
	return <li className='navigation-item'>{WITHOUT_CHILD}</li>;
};

export default memo(Item);
